import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../lib/constants';
import { useAuth } from '../lib/auth';

const TOTAL_QUESTIONS = 15;
const TIME_LIMIT      = 15 * 60; // 15 minutes in seconds

// ── Anti-copy ─────────────────────────────────────────────────────
function useAntiCopy(active) {
  useEffect(() => {
    if (!active) return;
    const prevent     = (e) => e.preventDefault();
    const preventKeys = (e) => {
      if ((e.ctrlKey || e.metaKey) && ['c','a','u','s','p'].includes(e.key.toLowerCase())) e.preventDefault();
      if (e.key === 'F12') e.preventDefault();
    };
    document.addEventListener('contextmenu', prevent);
    document.addEventListener('keydown', preventKeys);
    document.addEventListener('copy', prevent);
    document.addEventListener('selectstart', prevent);
    return () => {
      document.removeEventListener('contextmenu', prevent);
      document.removeEventListener('keydown', preventKeys);
      document.removeEventListener('copy', prevent);
      document.removeEventListener('selectstart', prevent);
    };
  }, [active]);

  // Warn when tab is switched during quiz (but don't redirect)
  useEffect(() => {
    if (!active) return;
    const handleVisibility = () => {
      if (document.hidden) {
        // Just log - timer keeps running, that's punishment enough
        console.log('Quiz: tab switched at', new Date().toISOString());
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [active]);
}

// ── Circular Timer ────────────────────────────────────────────────
function TimerDisplay({ secondsLeft }) {
  const mins   = Math.floor(secondsLeft / 60);
  const secs   = secondsLeft % 60;
  const pct    = (secondsLeft / TIME_LIMIT) * 100;
  const isCrit = secondsLeft <= 30;
  const isLow  = secondsLeft <= 60;
  const color  = isCrit ? '#E85252' : isLow ? '#F4A83A' : '#7EB8D4';
  return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:3 }}>
      <div style={{ position:'relative', width:60, height:60 }}>
        <svg width="60" height="60" style={{ transform:'rotate(-90deg)' }}>
          <circle cx="30" cy="30" r="25" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="5"/>
          <circle cx="30" cy="30" r="25" fill="none" stroke={color} strokeWidth="5" strokeLinecap="round"
            strokeDasharray={`${2*Math.PI*25}`}
            strokeDashoffset={`${2*Math.PI*25*(1-pct/100)}`}
            style={{ transition:'stroke-dashoffset 1s linear, stroke 0.3s' }}/>
        </svg>
        <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <span style={{ fontSize:13, fontWeight:700, color:'#F5F0E8', fontFamily:"'DM Sans'" }}>
            {String(mins).padStart(2,'0')}:{String(secs).padStart(2,'0')}
          </span>
        </div>
      </div>
      <span style={{ fontSize:9, color:'rgba(245,240,232,0.6)', textTransform:'uppercase', letterSpacing:0.5 }}>
        {isCrit ? '⚠ HURRY' : isLow ? '⚠ Low' : 'Time left'}
      </span>
    </div>
  );
}

// ── Send Email ────────────────────────────────────────────────────
async function sendResultsEmail(fellowName, questions, answers, score, pct) {
  const optLabel = (q, key) => ({ A:q.option_a, B:q.option_b, C:q.option_c, D:q.option_d, E:q.option_e }[key] || '—');
  const wrong   = questions.filter(q => (answers[q.id]||'').toUpperCase() !== q.correct_answer.toUpperCase());
  const correct = questions.filter(q => (answers[q.id]||'').toUpperCase() === q.correct_answer.toUpperCase());

  const wrongRows = wrong.map((q, i) => {
    const sel = (answers[q.id] || 'Not answered').toUpperCase();
    return `<tr style="background:${i%2===0?'#fff':'#fafafa'}">
      <td style="padding:9px 10px;border:1px solid #eee;font-size:12px;color:#333;">${q.question}</td>
      <td style="padding:9px 10px;border:1px solid #eee;font-size:11px;color:#1B7A9E;font-weight:600;">${q.category}</td>
      <td style="padding:9px 10px;border:1px solid #eee;font-size:12px;color:#E85252;">${sel} — ${optLabel(q,sel)}</td>
      <td style="padding:9px 10px;border:1px solid #eee;font-size:12px;color:#1E5C40;font-weight:600;">${q.correct_answer} — ${optLabel(q,q.correct_answer)}</td>
      <td style="padding:9px 10px;border:1px solid #eee;font-size:11px;color:#666;font-style:italic;">${q.explanation||'—'}</td>
    </tr>`;
  }).join('');

  const html = `<div style="font-family:Georgia,serif;max-width:900px;margin:0 auto;padding:24px;background:#F7F4F0;">
    <div style="background:#1A3A5C;padding:20px 24px;border-radius:8px;margin-bottom:20px;">
      <div style="color:#7EB8D4;font-size:18px;margin-bottom:4px;">✦ HPM Quiz Results</div>
      <div style="color:rgba(245,240,232,0.7);font-size:12px;">King Hussein Cancer Center · Automated Report</div>
    </div>
    <div style="background:#fff;border-radius:8px;padding:20px 24px;margin-bottom:16px;">
      <table style="width:100%;border-collapse:collapse;">
        <tr><td style="padding:8px 0;font-size:14px;color:#555;border-bottom:1px solid #eee;">Fellow</td><td style="padding:8px 0;font-size:15px;font-weight:700;color:#1A3A5C;border-bottom:1px solid #eee;">${fellowName}</td></tr>
        <tr><td style="padding:8px 0;font-size:14px;color:#555;border-bottom:1px solid #eee;">Score</td><td style="padding:8px 0;font-size:22px;font-weight:700;color:#1A3A5C;border-bottom:1px solid #eee;">${score}/${TOTAL_QUESTIONS} &nbsp;<span style="font-size:16px;color:#1B7A9E;">(${pct}%)</span></td></tr>
        <tr><td style="padding:8px 0;font-size:14px;color:#1E5C40;">✓ Correct</td><td style="padding:8px 0;font-size:14px;font-weight:700;color:#1E5C40;">${correct.length} questions</td></tr>
        <tr><td style="padding:8px 0;font-size:14px;color:#E85252;">✗ Incorrect</td><td style="padding:8px 0;font-size:14px;font-weight:700;color:#E85252;">${wrong.length} questions</td></tr>
      </table>
    </div>
    ${wrong.length > 0 ? `
    <div style="background:#fff;border-radius:8px;padding:20px 24px;margin-bottom:16px;">
      <div style="font-size:15px;font-weight:700;color:#1A3A5C;margin-bottom:12px;">❌ Incorrect Answers (${wrong.length})</div>
      <table style="width:100%;border-collapse:collapse;">
        <thead><tr style="background:#1A3A5C;">
          <th style="padding:9px 10px;text-align:left;font-size:11px;color:#fff;border:1px solid #1A3A5C;">Question</th>
          <th style="padding:9px 10px;text-align:left;font-size:11px;color:#fff;border:1px solid #1A3A5C;">Category</th>
          <th style="padding:9px 10px;text-align:left;font-size:11px;color:#fff;border:1px solid #1A3A5C;">Fellow's Answer</th>
          <th style="padding:9px 10px;text-align:left;font-size:11px;color:#fff;border:1px solid #1A3A5C;">Correct Answer</th>
          <th style="padding:9px 10px;text-align:left;font-size:11px;color:#fff;border:1px solid #1A3A5C;">Explanation</th>
        </tr></thead>
        <tbody>${wrongRows}</tbody>
      </table>
    </div>` : `<div style="background:#F0FFF6;border:1px solid #A8DCC0;border-radius:8px;padding:14px;text-align:center;font-size:15px;color:#1E5C40;font-weight:600;">🎉 Perfect score!</div>`}
    <div style="text-align:center;font-size:10px;color:#aaa;margin-top:12px;">King Hussein Cancer Center · HPM Fellowship Program</div>
  </div>`;

  try {
    await supabase.rpc('send_custom_email', {
      recipient_email: 'fadinaf@gmail.com',
      recipient_name:  'Dr. Fadi Abu-Farsakh',
      email_subject:   `HPM Quiz — ${fellowName} — ${score}/${TOTAL_QUESTIONS} (${pct}%)`,
      email_html:      html,
    });
  } catch(e) { console.log('Email error:', e); }
}

// ── Main Quiz Component ───────────────────────────────────────────
export default function Quiz({ isMobile, onFinish }) {
  const { user, profile } = useAuth();

  // phase: loading | intro | active | locked
  const [phase, setPhase]         = useState('loading');
  const [questions, setQuestions] = useState([]);
  const [qIndex, setQIndex]       = useState(0);
  const [answers, setAnswers]     = useState({});
  const [timeLeft, setTimeLeft]   = useState(TIME_LIMIT);
  const [attemptId, setAttemptId] = useState(null);
  const [finalResult, setFinalResult] = useState(null);
  const [loading, setLoading]     = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const timerRef = useRef(null);

  useAntiCopy(phase === 'active');

  // On mount — check if already completed
  useEffect(() => {
    if (!user) return;
    checkStatus();
  }, [user]);

  const checkStatus = async () => {
    const { data } = await supabase
      .from('quiz_attempts')
      .select('*')
      .eq('user_id', user.id)
      .in('status', ['completed', 'timed_out'])
      .order('started_at', { ascending: false })
      .limit(1);

    if (data && data.length > 0) {
      const done = data[0];
      setFinalResult({
        score: done.score || 0,
        pct:   Math.round(done.percentage || 0),
        status: done.status,
      });
      setPhase('locked');
    } else {
      setPhase('intro');
    }
  };

  const stopTimer = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  };

  const finishQuiz = useCallback(async (finalAnswers, timedOut = false) => {
    stopTimer();
    setSubmitting(true);

    const score = questions.filter(q =>
      (finalAnswers[q.id] || '').toUpperCase() === q.correct_answer.toUpperCase()
    ).length;
    const pct = Math.round((score / TOTAL_QUESTIONS) * 100);

    // Save to database
    if (attemptId) {
      await supabase.from('quiz_attempts').update({
        answers: finalAnswers,
        score,
        total_questions: TOTAL_QUESTIONS,
        percentage: pct,
        status: timedOut ? 'timed_out' : 'completed',
        completed_at: new Date().toISOString(),
      }).eq('id', attemptId);
    }

    // Send email to director
    await sendResultsEmail(profile?.full_name || 'Fellow', questions, finalAnswers, score, pct);

    setFinalResult({ score, pct, status: timedOut ? 'timed_out' : 'completed' });
    setSubmitting(false);
    setPhase('locked');
  }, [questions, attemptId, profile]);

  // Timer
  useEffect(() => {
    if (phase !== 'active') return;
    timerRef.current = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) {
          clearInterval(timerRef.current);
          finishQuiz(answers, true);
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return stopTimer;
  }, [phase, finishQuiz, answers]);

  const startQuiz = async () => {
    setLoading(true);
    const { data } = await supabase.from('quiz_questions').select('*').eq('is_active', true);
    if (!data?.length) { setLoading(false); return; }

    const shuffle = arr => [...arr].sort(() => Math.random() - 0.5);
    const easy   = shuffle(data.filter(q => q.difficulty <= 2));
    const medium = shuffle(data.filter(q => q.difficulty === 3));
    const hard   = shuffle(data.filter(q => q.difficulty >= 4));

    let selected = [...easy.slice(0,3), ...medium.slice(0,8), ...hard.slice(0,4)];
    if (selected.length < TOTAL_QUESTIONS) {
      const used = new Set(selected.map(q => q.id));
      const rest = shuffle(data.filter(q => !used.has(q.id)));
      selected   = [...selected, ...rest.slice(0, TOTAL_QUESTIONS - selected.length)];
    }
    selected = shuffle(selected).slice(0, TOTAL_QUESTIONS);

    // Create attempt record
    const { data: att } = await supabase.from('quiz_attempts').insert({
      user_id:      user.id,
      fellow_name:  profile?.full_name,
      fellow_email: user?.email,
      part:         1,
      questions:    selected.map(q => q.id),
      time_limit_seconds: TIME_LIMIT,
    }).select().single();

    setAttemptId(att?.id);
    setQuestions(selected);
    setAnswers({});
    setQIndex(0);
    setTimeLeft(TIME_LIMIT);
    setLoading(false);
    setPhase('active');
  };

  const handleSubmit = async () => {
    const unanswered = questions.filter(q => !answers[q.id]).length;
    if (unanswered > 0 && !window.confirm(`You have ${unanswered} unanswered question${unanswered > 1 ? 's' : ''}. Submit anyway?`)) return;
    await finishQuiz(answers, false);
  };

  // ── LOADING ──────────────────────────────────────────────────
  if (phase === 'loading') return (
    <div style={s.page}>
      <div style={{ padding:80, textAlign:'center', color:'#888' }}>
        <div style={{ fontSize:32, marginBottom:12 }}>📝</div>
        Checking your quiz status…
      </div>
    </div>
  );

  // ── INTRO ────────────────────────────────────────────────────
  if (phase === 'intro') return (
    <div style={s.page}>
      <div style={{ maxWidth:540, margin:'0 auto', padding: isMobile ? 16 : 40 }}>
        <div style={s.card}>
          <div style={{ textAlign:'center', marginBottom:24 }}>
            <div style={{ fontSize:44, marginBottom:10 }}>📝</div>
            <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:28, color:'#1A3A5C', marginBottom:4 }}>HPM Fellowship Quiz</div>
            <div style={{ fontSize:13, color:'#888' }}>King Hussein Cancer Center</div>
          </div>

          <div style={{ background:'#EEF3FA', borderRadius:10, padding:'14px 18px', marginBottom:16 }}>
            <div style={{ fontSize:11, fontWeight:700, color:'#1A3A5C', marginBottom:10, textTransform:'uppercase', letterSpacing:0.5 }}>📋 Quiz Structure</div>
            {[
              { icon:'🔢', text:`${TOTAL_QUESTIONS} questions — to be completed in one session` },
              { icon:'⏱', text:'15 minutes — timer starts immediately and cannot be paused' },
              { icon:'🔀', text:'Questions randomly selected from the question bank' },
              { icon:'↔', text:'Navigate freely between questions during the quiz' },
              { icon:'🔒', text:'The quiz can only be taken once — no retakes allowed' },
            ].map((item, i) => (
              <div key={i} style={{ display:'flex', gap:10, marginBottom:7 }}>
                <span>{item.icon}</span>
                <span style={{ fontSize:13, color:'#333', lineHeight:1.5 }}>{item.text}</span>
              </div>
            ))}
          </div>

          <div style={{ background:'#FFF0F0', border:'1px solid #F4A8A8', borderRadius:10, padding:'14px 18px', marginBottom:16 }}>
            <div style={{ fontSize:11, fontWeight:700, color:'#C42A2A', marginBottom:10, textTransform:'uppercase', letterSpacing:0.5 }}>⚠ Important Rules</div>
            {[
              'Once started, the timer cannot be paused or stopped',
              'Do NOT navigate to other screens or open other windows',
              'Do NOT switch browser tabs or open other applications',
              'Text copying and right-click are disabled during the quiz',
              'The quiz auto-submits when time runs out',
              'Results are automatically sent to the Program Director',
            ].map((rule, i) => (
              <div key={i} style={{ display:'flex', gap:8, marginBottom:5 }}>
                <span style={{ color:'#C42A2A', fontWeight:700, flexShrink:0 }}>•</span>
                <span style={{ fontSize:13, color:'#8A2D2D', lineHeight:1.5 }}>{rule}</span>
              </div>
            ))}
          </div>

          <button onClick={startQuiz} disabled={loading}
            style={{ ...s.primaryBtn, background: loading ? '#888' : '#1A3A5C', marginBottom:10 }}>
            {loading ? 'Loading questions…' : 'I understand — Start Quiz →'}
          </button>
          <button onClick={onFinish} style={s.secondaryBtn}>← Back</button>
        </div>
      </div>
    </div>
  );

  // ── LOCKED — quiz completed ──────────────────────────────────
  if (phase === 'locked') return (
    <div style={s.page}>
      <div style={{ maxWidth:500, margin:'0 auto', padding: isMobile ? 16 : 40 }}>
        <div style={s.card}>
          <div style={{ textAlign:'center', marginBottom:24 }}>
            <div style={{ fontSize:48, marginBottom:10 }}>🏆</div>
            <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:26, color:'#1A3A5C', marginBottom:6 }}>
              Assessment Complete!
            </div>
            <div style={{ fontSize:13, color:'#888', lineHeight:1.6 }}>
              You have completed the HPM Fellowship Quiz. Best of luck in your fellowship journey!
            </div>
          </div>

          {/* Score */}
          <div style={{ background:'#1A3A5C', borderRadius:12, padding:'20px 24px', marginBottom:16, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <div>
              <div style={{ fontSize:10, color:'#7EB8D4', textTransform:'uppercase', letterSpacing:1, marginBottom:4 }}>Your Score</div>
              <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:46, color:'#F5F0E8', lineHeight:1 }}>
                {finalResult?.score}<span style={{ fontSize:20, opacity:0.5 }}>/{TOTAL_QUESTIONS}</span>
              </div>
            </div>
            <div style={{ textAlign:'right' }}>
              <div style={{ fontSize:10, color:'#7EB8D4', textTransform:'uppercase', letterSpacing:1, marginBottom:4 }}>Percentage</div>
              <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:46, color:'#F5F0E8', lineHeight:1 }}>
                {finalResult?.pct}<span style={{ fontSize:20, opacity:0.5 }}>%</span>
              </div>
              <div style={{ fontSize:12, fontWeight:600, marginTop:4,
                color: (finalResult?.pct||0) >= 90 ? '#7EB8D4' : (finalResult?.pct||0) >= 70 ? '#A8DCC0' : (finalResult?.pct||0) >= 50 ? '#F4DFA8' : '#F4A8A8' }}>
                {(finalResult?.pct||0) >= 90 ? '★ Outstanding' : (finalResult?.pct||0) >= 70 ? '✓ Good' : (finalResult?.pct||0) >= 50 ? '~ Satisfactory' : 'Needs Improvement'}
              </div>
            </div>
          </div>

          {finalResult?.status === 'timed_out' && (
            <div style={{ background:'#FFF0F0', border:'1px solid #F4A8A8', borderRadius:8, padding:'10px 14px', marginBottom:16, fontSize:12, color:'#8A2D2D', textAlign:'center' }}>
              ⏱ Time ran out — quiz was auto-submitted
            </div>
          )}

          <div style={{ background:'#F7F4F0', border:'1px solid #E0DDD8', borderRadius:10, padding:'14px 18px', marginBottom:20, textAlign:'center' }}>
            <div style={{ fontSize:20, marginBottom:8 }}>🔒</div>
            <div style={{ fontSize:13, color:'#555', lineHeight:1.7 }}>
              Your results have been recorded and sent to the Program Director. No further attempts are permitted.
            </div>
          </div>

          <button onClick={onFinish} style={s.primaryBtn}>← Return to Home</button>
        </div>
      </div>
    </div>
  );

  // ── ACTIVE QUIZ ──────────────────────────────────────────────
  const q           = questions[qIndex];
  const answered    = Object.keys(answers).length;
  const selectedAns = answers[q?.id];
  const isLow       = timeLeft <= 60;
  const isCrit      = timeLeft <= 30;
  const opts        = q ? [
    { key:'A', text: q.option_a },
    { key:'B', text: q.option_b },
    { key:'C', text: q.option_c },
    { key:'D', text: q.option_d },
    ...(q.option_e ? [{ key:'E', text: q.option_e }] : []),
  ] : [];

  if (!q) return <div style={{ padding:60, textAlign:'center', color:'#888' }}>Loading…</div>;

  return (
    <div style={{ ...s.page, userSelect:'none', WebkitUserSelect:'none' }}>

      {/* Sticky header with timer */}
      <div style={{ background:'#1A3A5C', padding: isMobile ? '12px 16px' : '12px 32px', position:'sticky', top:0, zIndex:10 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>
            <div style={{ fontSize:10, color:'#7EB8D4', textTransform:'uppercase', letterSpacing:1 }}>HPM Fellowship Quiz</div>
            <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize: isMobile ? 15 : 18, color:'#F5F0E8' }}>
              Question {qIndex + 1} <span style={{ opacity:0.5 }}>of {TOTAL_QUESTIONS}</span>
            </div>
          </div>
          <TimerDisplay secondsLeft={timeLeft} />
        </div>
        <div style={{ height:3, background:'rgba(255,255,255,0.15)', borderRadius:4, marginTop:8 }}>
          <div style={{ height:'100%', borderRadius:4, background:'#7EB8D4', width:`${((qIndex+1)/TOTAL_QUESTIONS)*100}%`, transition:'width 0.3s' }}/>
        </div>
      </div>

      {/* Warning banners */}
      {isCrit && <div style={{ background:'#E85252', padding:'7px 20px', textAlign:'center', fontSize:13, fontWeight:700, color:'#fff' }}>⚠ Less than 30 seconds — submit now!</div>}
      {isLow && !isCrit && <div style={{ background:'#F4A83A', padding:'6px 20px', textAlign:'center', fontSize:12, fontWeight:600, color:'#fff' }}>⏱ Less than 1 minute remaining!</div>}

      <div style={{ maxWidth:780, margin:'0 auto', padding: isMobile ? '14px' : '22px 28px', paddingBottom: isMobile ? 140 : 100 }}>

        {/* Category & difficulty */}
        <div style={{ display:'flex', gap:8, marginBottom:14, flexWrap:'wrap' }}>
          <span style={{ fontSize:11, background:'#EEF3FA', color:'#1B7A9E', padding:'3px 10px', borderRadius:20, fontWeight:500 }}>{q.category}</span>
          <span style={{ fontSize:11, background:'#F7F4F0', color:'#888', padding:'3px 10px', borderRadius:20 }}>{'⭐'.repeat(q.difficulty)}</span>
        </div>

        {/* Question text */}
        <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize: isMobile ? 18 : 22, color:'#1A2535', lineHeight:1.7, marginBottom:22 }}>
          {q.question}
        </div>

        {/* Answer options */}
        <div style={{ display:'flex', flexDirection:'column', gap:10, marginBottom:22 }}>
          {opts.map(opt => {
            const isSelected = selectedAns === opt.key;
            return (
              <button key={opt.key}
                onClick={() => setAnswers(prev => ({ ...prev, [q.id]: opt.key }))}
                style={{ display:'flex', alignItems:'flex-start', gap:14, padding:'13px 16px', borderRadius:10, border:`2px solid ${isSelected ? '#1A3A5C' : '#E0DDD8'}`, background: isSelected ? '#EEF3FA' : '#fff', cursor:'pointer', textAlign:'left', fontFamily:"'DM Sans'", transition:'all 0.12s', boxShadow: isSelected ? '0 2px 10px rgba(26,58,92,0.12)' : 'none' }}>
                <div style={{ width:28, height:28, borderRadius:'50%', flexShrink:0, background: isSelected ? '#1A3A5C' : '#F0EDE8', display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:700, color: isSelected ? '#fff' : '#888' }}>
                  {opt.key}
                </div>
                <div style={{ fontSize: isMobile ? 14 : 15, color: isSelected ? '#1A3A5C' : '#333', lineHeight:1.6, fontWeight: isSelected ? 500 : 400, flex:1 }}>
                  {opt.text}
                </div>
              </button>
            );
          })}
        </div>

        {/* Question dot navigator */}
        <div style={{ marginBottom:10 }}>
          <div style={{ fontSize:11, color:'#888', marginBottom:7 }}>{answered} of {TOTAL_QUESTIONS} answered</div>
          <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
            {questions.map((qu, i) => (
              <button key={qu.id} onClick={() => setQIndex(i)}
                style={{ width:32, height:32, borderRadius:7, border:`2px solid ${i === qIndex ? '#1A3A5C' : answers[qu.id] ? '#1B7A9E' : '#E0DDD8'}`, background: i === qIndex ? '#1A3A5C' : answers[qu.id] ? 'rgba(27,122,158,0.08)' : '#fff', color: i === qIndex ? '#fff' : answers[qu.id] ? '#1B7A9E' : '#aaa', fontSize:12, fontWeight:600, cursor:'pointer', fontFamily:"'DM Sans'" }}>
                {i + 1}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom navigation */}
      <div style={{ position:'fixed', bottom:0, left:0, right:0, background:'#fff', borderTop:'1px solid #E8E4E0', padding:'12px 20px', paddingBottom:'calc(12px + env(safe-area-inset-bottom))', display:'flex', gap:10, zIndex:300, boxShadow:'0 -4px 12px rgba(0,0,0,0.08)' }}>
        <button onClick={() => setQIndex(i => Math.max(0, i - 1))} disabled={qIndex === 0}
          style={{ padding:'11px 18px', background:'#F0EDE8', border:'none', borderRadius:8, fontSize:14, cursor: qIndex === 0 ? 'default' : 'pointer', opacity: qIndex === 0 ? 0.4 : 1, fontFamily:"'DM Sans'" }}>
          ← Back
        </button>
        {qIndex < TOTAL_QUESTIONS - 1
          ? <button onClick={() => setQIndex(i => i + 1)}
              style={{ flex:1, padding:'11px', background:'#1A3A5C', color:'#fff', border:'none', borderRadius:8, fontSize:14, fontWeight:600, cursor:'pointer', fontFamily:"'DM Sans'" }}>
              Next →
            </button>
          : <button onClick={handleSubmit} disabled={submitting}
              style={{ flex:1, padding:'11px', background:'#1E5C40', color:'#fff', border:'none', borderRadius:8, fontSize:14, fontWeight:600, cursor:'pointer', fontFamily:"'DM Sans'", opacity: submitting ? 0.7 : 1 }}>
              {submitting ? 'Submitting…' : '✓ Submit Quiz'}
            </button>}
      </div>
    </div>
  );
}

const s = {
  page:         { background:'#F7F4F0', minHeight:'100vh', fontFamily:"'DM Sans',sans-serif" },
  card:         { background:'#fff', borderRadius:14, padding:24, boxShadow:'0 4px 20px rgba(26,58,92,0.1)', border:'1px solid rgba(26,58,92,0.07)' },
  primaryBtn:   { width:'100%', padding:'13px', background:'#1A3A5C', color:'#fff', border:'none', borderRadius:10, fontSize:15, fontWeight:600, cursor:'pointer', fontFamily:"'DM Sans'" },
  secondaryBtn: { width:'100%', padding:'11px', background:'#EEE', color:'#555', border:'none', borderRadius:10, fontSize:14, cursor:'pointer', fontFamily:"'DM Sans'" },
};
